// R49 — THE SHELVES DOOR: HOW A FINISHED READING IS HANDED TO THE MEMBER WHEREVER SHE IS STANDING.
//
// **THE MEASUREMENT THAT PICKED THIS ONE, AND IT IS AN EXACT BICONDITIONAL WITH ONE EXCEPTION.**
// Every custom event this site dispatches is DECLARED ONCE as an exported constant beside the thing
// that owns it — `twelve:locked-door` (src/lib/locked-door.ts), `twelve:overlay-tab` and
// `twelve:close-overlays` (MobileNav.tsx), `twelve:open-cauldron` (CauldronDoor.tsx),
// `twelve:ask-lala-reading` (AskLalaAboutButton.tsx). **`twelve:open-shelves` was the one exception,
// hand-typed THREE times inside App.tsx — at BOTH ends of the crossing** (the site-wide generation
// follower dispatches it, the shelves drawer listens for it) plus three verification scripts typing
// it a fourth, fifth and sixth time. R13/R14/R15's shape: a law held across a boundary by nobody,
// where the fixture itself is a further copy of the answer key.
//
// **AND IT IS R26's POSITION EXACTLY** (the pointer's standing hunt: what in App.tsx is reachable
// only by a live browser because all it touches is `window`?). A dispatch and a listener are pure
// side effects, so a real Chrome was the only instrument that could reach any of this. The window is
// a PARAMETER here (R46's ruling, not CauldronDoor's global), so a gate RUNS the door and reads back
// what it dispatched — including the DROP, which has no visible symptom.
//
// **THE LAW WITH TEETH IS R18's DISTINCTION, AND R17 NAMED THIS EXACT SILENCE FROM THE OTHER END.**
// The request carries two facts and they are not the same fact: WHICH SHELF to open, and WHICH ITEM
// on it to open. R17 measured five generator kinds resolving to `room: null` on the server and wrote
// down what the client then did: *"'Open it' dispatches `room: undefined`, so the drawer opens on a
// bare room list, `loadRoom` never runs, and the pending-open effect is guarded by `!items.length` —
// no error, no log, no else."* R17 closed the SERVER's roster. This closes the client end: an item
// with no room is NOT OPENABLE — the drawer would latch a pending id against a shelf it never loads,
// and that id then sits waiting to resolve against some later, unrelated visit. `canOpenItem` says
// so out loud instead of the call site testing `detail.itemId` and hoping.
//
// Imports NOTHING (the entry requirement that lets `scripts/r49-shelves-door.mjs` load and RUN it).

export const OPEN_SHELVES_EVENT = "twelve:open-shelves";

export type ShelvesRequest = { room: string | null; itemId: string | null };

// What arrives on the wire is a `CustomEvent.detail` somebody else built, so it is normalized rather
// than trusted. An ABSENT room and an EMPTY room are the same fact (no shelf named); an id may be a
// number on the way out (`savedId`) and is always a string on the way in, because the drawer compares
// it with `String(item.id)`.
export function shelvesRequest(detail: unknown): ShelvesRequest {
  const d = (detail && typeof detail === "object" ? detail : {}) as Record<string, unknown>;
  const rawRoom = typeof d.room === "string" ? d.room.trim() : "";
  const rawItem = d.itemId === 0 || d.itemId ? String(d.itemId).trim() : "";
  return { room: rawRoom || null, itemId: rawItem || null };
}

export function shelvesRequestFrom(event: unknown): ShelvesRequest {
  const e = (event && typeof event === "object" ? event : {}) as { detail?: unknown };
  return shelvesRequest(e.detail);
}

// **AN ITEM WITH NO ROOM CANNOT BE OPENED, AND THAT IS ARITHMETIC RATHER THAN A PREFERENCE.** The
// drawer opens an item by loading its room's shelf and finding it in the list; with no room the list
// stays empty and the pending id never resolves. Latching it anyway is a flag with no owner.
export function canOpenItem(request: ShelvesRequest): boolean {
  return Boolean(request.itemId && request.room);
}

// The window shape this door needs, and nothing more — R46's ruling, and the same cast-through-
// `unknown` the locked-door interceptor uses so a real `Window` satisfies it at the call site.
// `CustomEvent` is taken off the window rather than off the global, because a gate's fake window is
// the whole point: a door that reaches for the ambient global cannot be RUN outside a browser.
export type ShelvesDoorWindow = {
  dispatchEvent: (event: unknown) => unknown;
  CustomEvent: new (type: string, init?: { detail?: unknown }) => unknown;
};

const browserWindow = () => (typeof window === "undefined" ? null : (window as unknown as ShelvesDoorWindow));

// The door itself. Returns whether the request actually went out, so a caller on a server render (or
// a browser with no CustomEvent) gets an answer rather than an exception — R18 again: "it did not go"
// and "it went" are two facts, and a door that throws makes them one.
export function openShelves(
  request: { room?: string | null; itemId?: string | number | null },
  win: ShelvesDoorWindow | null = browserWindow(),
): boolean {
  const detail = shelvesRequest(request);
  if (!win || typeof win.dispatchEvent !== "function" || typeof win.CustomEvent !== "function") return false;
  win.dispatchEvent(new win.CustomEvent(OPEN_SHELVES_EVENT, { detail }));
  return true;
}
